import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import AutoTextarea from "../components/AutoTextarea";
import { Cpu, ListChecks, Save, Plus, Trash2, Loader2, Check, AlertTriangle, ChevronDown, Info, GraduationCap, Plug, ExternalLink } from "lucide-react";

export default function SystemSettingsPage() {
  const [tab, setTab] = useState("models");

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1200px]" data-testid="settings-page">
        <div className="label-xs mb-2">Administration · Configuration système</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Paramètres avancés
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Réservé au super-administrateur. Ces paramètres pilotent la génération IA des rattrapages.
          Toute modification s'applique immédiatement aux prochains rattrapages générés.
        </p>

        <div className="mt-8 inline-flex items-center bg-white border border-slate-200 rounded-sm p-0.5">
          <TabBtn active={tab === "models"} onClick={() => setTab("models")} testId="tab-models">
            <Cpu size={13} /> Modèles IA
          </TabBtn>
          <TabBtn active={tab === "rules"} onClick={() => setTab("rules")} testId="tab-rules">
            <ListChecks size={13} /> Règles QCM
          </TabBtn>
          <TabBtn active={tab === "lms"} onClick={() => setTab("lms")} testId="tab-lms">
            <GraduationCap size={13} /> LMS LearnWorlds
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === "models" ? <ModelsPanel /> : tab === "rules" ? <RulesPanel /> : <LmsPanel />}
        </div>
      </div>
    </AppShell>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

/* ---------------- Models ---------------- */
function ModelsPanel() {
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          api.get("/admin/settings/models"),
          api.get("/admin/settings/providers"),
        ]);
        setItems(m.data);
        setProviders(p.data);
      } catch (e) {
        setErr(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (idx, patch) => setItems((xs) => xs.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));
  const add = () => setItems((xs) => [
    ...xs,
    { key: "", provider: providers[0] || "openai", model: "", label: "", description: "", enabled: true },
  ]);

  const save = async () => {
    setSaving(true); setErr(""); setDone(false);
    try {
      const { data } = await api.put("/admin/settings/models", { items });
      setItems(data);
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <div data-testid="models-panel">
      <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Important :</strong> l'identifiant du modèle (colonne « Modèle ») doit correspondre exactement à
          celui exposé par le fournisseur (ex. <code className="px-1 bg-amber-100 rounded">gpt-5.2</code>,
          <code className="px-1 bg-amber-100 rounded mx-1">claude-sonnet-4-5-20250929</code>,
          <code className="px-1 bg-amber-100 rounded">gemini-3-flash</code>). Un identifiant invalide entraînera
          un échec de génération au moment de l'appel. Désactivez un modèle plutôt que de le supprimer si vous
          voulez le réactiver plus tard.
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {items.map((it, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-md p-5" data-testid={`model-row-${idx}`}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
              <div className="md:col-span-2">
                <label className="label-xs block mb-1">Clé interne</label>
                <input
                  value={it.key}
                  onChange={(e) => update(idx, { key: e.target.value })}
                  placeholder="ex. gpt-5.2"
                  data-testid={`model-key-${idx}`}
                  className="input font-mono"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-xs block mb-1">Fournisseur</label>
                <div className="relative">
                  <select
                    value={it.provider}
                    onChange={(e) => update(idx, { provider: e.target.value })}
                    data-testid={`model-provider-${idx}`}
                    className="input appearance-none pr-8"
                  >
                    {providers.map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="label-xs block mb-1">Modèle</label>
                <input
                  value={it.model}
                  onChange={(e) => update(idx, { model: e.target.value })}
                  placeholder="ex. claude-sonnet-4-5-20250929"
                  data-testid={`model-id-${idx}`}
                  className="input font-mono"
                />
              </div>
              <div className="md:col-span-3">
                <label className="label-xs block mb-1">Libellé affiché</label>
                <input
                  value={it.label}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="ex. OpenAI GPT-5.2"
                  data-testid={`model-label-${idx}`}
                  className="input"
                />
              </div>
              <div className="md:col-span-2 flex flex-col items-start gap-2">
                <label className="label-xs">Statut</label>
                <button
                  type="button"
                  onClick={() => update(idx, { enabled: !it.enabled })}
                  data-testid={`model-enabled-${idx}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm border ${
                    it.enabled ? "bg-green-50 text-[#15803d] border-green-200" : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${it.enabled ? "bg-[#15803d]" : "bg-slate-400"}`} />
                  {it.enabled ? "Activé" : "Désactivé"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start mt-3">
              <div className="md:col-span-11">
                <label className="label-xs block mb-1">Description (affichée au staff)</label>
                <input
                  value={it.description}
                  onChange={(e) => update(idx, { description: e.target.value })}
                  placeholder="Ex. Recommandé pour la rigueur scientifique"
                  data-testid={`model-desc-${idx}`}
                  className="input"
                />
              </div>
              <div className="md:col-span-1 flex justify-end items-end">
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="inline-flex items-center gap-1 text-xs text-[#b91c1c] border border-red-200 px-2 py-1.5 rounded-sm hover:bg-red-50"
                  data-testid={`model-remove-${idx}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={add}
          data-testid="add-model-btn"
          className="w-full flex items-center justify-center gap-1.5 text-sm border border-dashed border-slate-300 rounded-sm py-3 text-slate-700 hover:bg-white"
        >
          <Plus size={14} /> Ajouter un modèle
        </button>
      </div>

      {err && <div className="mt-4 text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="models-error">{err}</div>}
      {done && <div className="mt-4 text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-2 rounded-sm flex items-center gap-2"><Check size={14} /> Modèles enregistrés.</div>}

      <div className="mt-5 flex items-center justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-models-btn"
          className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer les modèles
        </button>
      </div>

      <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.85rem; border: 1px solid #cbd5e1; border-radius: 2px; background: white; }
        .input:focus { outline: none; border-color: #0f172a; box-shadow: 0 0 0 1px #0f172a; }`}</style>
    </div>
  );
}

/* ---------------- Rules ---------------- */
function RulesPanel() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/qcm-rules");
        setRules(data);
      } catch (e) {
        setErr(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (patch) => setRules((r) => ({ ...r, ...patch }));

  const save = async () => {
    setSaving(true); setErr(""); setDone(false);
    try {
      const { data } = await api.put("/admin/settings/qcm-rules", rules);
      setRules(data);
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !rules) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <div data-testid="rules-panel" className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="label-xs mb-4">Structure de l'examen</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <NumField label="Questions" value={rules.num_questions} onChange={(v) => set({ num_questions: v })} testId="num-questions" min={1} max={30} />
          <NumField label="Propositions" value={rules.num_options} onChange={(v) => set({ num_options: v })} testId="num-options" min={2} max={8} />
          <NumField label="Durée (min)" value={rules.duration_minutes} onChange={(v) => set({ duration_minutes: v })} testId="duration" min={1} />
          <NumField label="Min/question" value={rules.time_per_question_minutes} onChange={(v) => set({ time_per_question_minutes: v })} testId="tpq" min={1} />
        </div>
        <div className="mt-3 flex items-start gap-2 text-xs text-slate-600 bg-blue-50/60 border border-blue-100 px-3 py-2 rounded-sm">
          <Info size={13} className="text-[#2563eb] mt-0.5 shrink-0" />
          <span>Ces paramètres sont injectés dans le prompt système. Le LLM est tenu de produire exactement ce nombre de questions et de propositions.</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="label-xs">Consignes pédagogiques (éditables)</div>
          <div className="text-xs text-slate-500">{(rules.pedagogical_guidelines || "").length} caractères</div>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Ces consignes sont ajoutées au prompt système juste après les contraintes structurelles. Vous pouvez
          ajuster le ton, le niveau, les exigences (présence de code, niveau de calcul, format des questions,
          interdiction de simples questions de cours, etc.).
        </p>
        <AutoTextarea
          value={rules.pedagogical_guidelines || ""}
          onChange={(e) => set({ pedagogical_guidelines: e.target.value })}
          minRows={10}
          data-testid="guidelines-input"
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 leading-relaxed font-mono"
        />
      </div>

      {err && <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="rules-error">{err}</div>}
      {done && <div className="text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-2 rounded-sm flex items-center gap-2"><Check size={14} /> Règles enregistrées.</div>}

      <div className="flex items-center justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-rules-btn"
          className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer les règles
        </button>
      </div>
    </div>
  );
}

/* ---------------- LMS (LearnWorlds) ---------------- */
function LmsPanel() {
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/lms");
        setCfg(data);
      } catch (e) {
        setErr(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const payload = () => ({
    enabled: Boolean(cfg?.enabled),
    school_domain: cfg?.school_domain || "",
    client_id: cfg?.client_id || "",
    client_secret: secret,
  });

  const save = async () => {
    setSaving(true); setErr(""); setDone(false); setTestResult(null);
    try {
      const { data } = await api.put("/admin/settings/lms", payload());
      setCfg(data);
      setSecret("");
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true); setErr(""); setTestResult(null); setDone(false);
    try {
      const { data } = await api.post("/admin/settings/lms/test", payload(), { timeout: 90000 });
      setTestResult(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <div data-testid="lms-panel" className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-xs mb-1">Intégration LMS</div>
            <div className="font-semibold text-slate-900">LearnWorlds</div>
            <p className="text-xs text-slate-600 mt-1 max-w-xl">
              Permet au staff de piocher directement les cours du LMS au lieu d'uploader des fichiers.
              Récupérez le Client ID et le Client Secret dans l'admin LearnWorlds :{" "}
              <a
                href="https://estiam.learnworlds.com/admin/api/"
                target="_blank"
                rel="noreferrer"
                className="text-[#2563eb] font-semibold inline-flex items-center gap-0.5 hover:underline"
              >
                Settings → Developers → API <ExternalLink size={10} />
              </a>
            </p>
          </div>
          <button
            type="button"
            onClick={() => set({ enabled: !cfg.enabled })}
            data-testid="lms-enabled-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm border shrink-0 ${
              cfg.enabled
                ? "bg-green-50 text-[#15803d] border-green-200"
                : "bg-slate-50 text-slate-500 border-slate-200"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${cfg.enabled ? "bg-[#15803d]" : "bg-slate-400"}`} />
            {cfg.enabled ? "Activé" : "Désactivé"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div>
            <label className="label-xs block mb-1">Domaine de l'école</label>
            <input
              value={cfg.school_domain || ""}
              onChange={(e) => set({ school_domain: e.target.value })}
              placeholder="estiam.learnworlds.com"
              data-testid="lms-domain-input"
              className="lms-input font-mono"
            />
          </div>
          <div>
            <label className="label-xs block mb-1">Client ID</label>
            <input
              value={cfg.client_id || ""}
              onChange={(e) => set({ client_id: e.target.value })}
              placeholder="votre Client ID LearnWorlds"
              data-testid="lms-client-id-input"
              className="lms-input font-mono"
            />
          </div>
          <div>
            <label className="label-xs block mb-1">
              Client Secret {cfg.client_secret_set && <span className="text-[#15803d] normal-case">· enregistré</span>}
            </label>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={cfg.client_secret_set ? "•••••••• (vide = inchangé)" : "votre Client Secret"}
              data-testid="lms-client-secret-input"
              className="lms-input font-mono"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-blue-50/60 border border-blue-100 px-3 py-2 rounded-sm">
          <Info size={13} className="text-[#2563eb] mt-0.5 shrink-0" />
          <span>
            Le secret est stocké côté serveur et n'est jamais renvoyé au navigateur. Le token OAuth2 est obtenu
            automatiquement (grant <code className="font-mono">client_credentials</code>) et mis en cache.
          </span>
        </div>
      </div>

      {err && (
        <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm flex items-start gap-2" data-testid="lms-error">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {done && (
        <div className="text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="lms-saved">
          <Check size={14} /> Configuration LMS enregistrée.
        </div>
      )}
      {testResult && (
        <div className="text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-3 rounded-sm" data-testid="lms-test-ok">
          <div className="flex items-center gap-2 font-semibold"><Check size={14} /> Connexion réussie · {testResult.courses_count} cours détecté(s)</div>
          {testResult.sample?.length > 0 && (
            <ul className="mt-2 ml-6 list-disc text-xs text-slate-700">
              {testResult.sample.map((t, i) => (<li key={i}>{t}</li>))}
            </ul>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          onClick={test}
          disabled={testing || saving}
          data-testid="lms-test-btn"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 rounded-sm text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Tester la connexion
        </button>
        <button
          onClick={save}
          disabled={saving || testing}
          data-testid="save-lms-btn"
          className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>

      <style>{`.lms-input { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.85rem; border: 1px solid #cbd5e1; border-radius: 2px; background: white; }
        .lms-input:focus { outline: none; border-color: #0f172a; box-shadow: 0 0 0 1px #0f172a; }`}</style>
    </div>
  );
}

function NumField({ label, value, onChange, testId, min, max }) {
  return (
    <div>
      <label className="label-xs block mb-1">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        data-testid={`num-${testId}`}
        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
      />
    </div>
  );
}
